export default class SSEClient {
  constructor(config) {
    this._source = null
    this.id = config.id
    this.name = config.name
    this.events = config.events
    this.store = config.store

    this.url = `${config.baseUrl}/${config.endpoint}?channel=${config.channel}`

    // Connection management
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = config.maxReconnectAttempts || 10
    this.reconnectTimeouts = [1000, 2000, 4000, 8000, 16000, 30000] // exponential backoff in ms
    this.isReconnecting = false
    this.connectionState = 'disconnected' // disconnected, connecting, connected, reconnecting
    this.reconnectTimer = null
    this.eventListeners = {} // Track event listeners for cleanup

    console.log('Connecting to SSE server...')
  }

  get source() {
    return this._source
  }

  connect() {
    // Update connection state
    this.connectionState = this.isReconnecting ? 'reconnecting' : 'connecting'

    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this._source = new window.EventSource(this.url, {
      withCredentials: false
    })

    return new Promise((resolve) => {
      this._source.onopen = () => {
        console.log('Successfully connected to SSE Server!')

        // Reset reconnection tracking on successful connection
        this.reconnectAttempts = 0
        this.isReconnecting = false
        this.connectionState = 'connected'

        this.store.commit('Connected')

        // Register event listeners and track them for cleanup
        if (typeof this.events === 'function') {
          this.eventListeners[this.name] = this.events
          this._source.addEventListener(this.name, this.events)
        } else {
          Object.keys(this.events).forEach((event) => {
            this.eventListeners[event] = this.events[event]
            this._source.addEventListener(event, this.events[event])
          })
        }

        resolve(this)
      }

      this._source.onerror = () => {
        // Only log on first failure or state changes
        if (!this.isReconnecting) {
          console.log('Failed to connect to SSE Server!')
        }

        // Update state
        this.connectionState = 'disconnected'
        this.store.commit('Disconnect')

        // Clean up the current connection
        this.cleanupConnection()

        // Check if we should attempt reconnection
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.isReconnecting = true

          // Calculate backoff delay
          const backoffIndex = Math.min(this.reconnectAttempts, this.reconnectTimeouts.length - 1)
          const delay = this.reconnectTimeouts[backoffIndex]

          this.reconnectAttempts++

          console.log(
            `Retrying SSE connection in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
          )

          // Schedule reconnection with exponential backoff
          this.reconnectTimer = setTimeout(() => {
            this.connect()
          }, delay)
        } else {
          console.log(
            `Maximum reconnection attempts (${this.maxReconnectAttempts}) reached. Giving up.`
          )
          this.isReconnecting = false
        }
      }
    })
  }

  cleanupConnection() {
    if (this._source !== null) {
      // Remove all event listeners
      Object.keys(this.eventListeners).forEach((event) => {
        this._source.removeEventListener(event, this.eventListeners[event])
      })
      this.eventListeners = {}

      // Close the connection
      this._source.close()
      this._source = null
    }
  }

  disconnect() {
    // Clear any pending reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // Reset connection state
    this.connectionState = 'disconnected'
    this.isReconnecting = false
    this.reconnectAttempts = 0

    // Clean up the connection
    this.cleanupConnection()
  }
}
